import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SCState {
  scStandards: string[];
  setScStandards: (value: string[]) => void;
  /** Map of standard code → description, e.g. { OCS: "Organic Content Standard", GRS: "Global Recycled Standard" } */
  scStandardDescriptions: Record<string, string>;
  setScStandardDescriptions: (value: Record<string, string>) => void;
  editSCInfo: { scId?: string; isParentStatus?: boolean } | null;
  setEditSCInfo: (value: { scId?: string; isParentStatus?: boolean } | null) => void;
  /** True when any facility in the SC has process code PR0035 — makes brandName + certifiedOrganizationApprovedClaims mandatory */
  hasPR0035: boolean;
  setHasPR0035: (value: boolean) => void;
  /** SC Last Audit Start Date — used as minDate for Facility Last Audit Date picker */
  scLastAuditStartDate: string;
  setScLastAuditStartDate: (value: string) => void;
  /** SC Last Audit Date — used as maxDate for Facility Last Audit Date picker */
  scLastAuditDate: string;
  setScLastAuditDate: (value: string) => void;

  // ── Cached SC details (GET_SC_DETAILS_V4) ──────────────────────────────────
  // Fetched once by the parent; child tabs read from here instead of re-fetching.
  // Cleared whenever SC_ID changes so stale data never leaks across SCs.
  scData: any | null;
  setScData: (value: any) => void;
  clearScData: () => void;

  // ── Facility List cache (GET_FACILITY_LIST_DETAILS_V4) ─────────────────────
  // Populated by any tab that fetches the list. Shared to avoid redundant list fetches.
  facilityListCache: any[] | null;
  setFacilityListCache: (list: any[]) => void;
  clearFacilityListCache: () => void;

  // ── Cache Invalidation Trigger ─────────────────────────────────────────────
  // Incremented on save/delete to force background prefetch hooks to re-run and sync caches.
  facilityCacheTick: number;
  incrementFacilityCacheTick: () => void;

  // ── Facility detail cache (GET_FACILITY_DETAILS_V4 per facility) ───────────
  // Populated on first Facility tab load; reused on return visits.
  // invalidateFacilityDetail(id) removes ONLY the changed facility so the
  // violation banners always see fresh data after a save, without re-fetching
  // every other facility.
  facilityDetailsCache: Record<string, any>;
  setCachedFacilityDetail: (facilityId: string, data: any) => void;
  invalidateFacilityDetail: (facilityId: string) => void;
  clearFacilityCache: () => void;

  // ── Per-tab error flags ────────────────────────────────────────────────────
  tabErrors: Record<string, boolean>;
  setTabError: (tabId: string, hasError: boolean) => void;
  clearTabErrors: () => void;

  // ── Saved claims from Tab 7 ────────────────────────────────────────────────
  // Written by the Claims tab when it loads data from the API.
  // Read by the parent stepper to proactively compute Tab 7 errors without
  // requiring the user to visit Tab 7 first.
  savedClaims: string[];
  setSavedClaims: (claims: string[]) => void;
  clearSavedClaims: () => void;

  // ── Backend Validation Errors (422 responses) ──────────────────────────────
  // Stores error messages mapped by tab ID so the correct tab can display them.
  backendValidationErrors: Record<string, string[]>;
  setBackendValidationErrors: (tabId: string, messages: string[]) => void;
  clearBackendValidationErrors: () => void;

  // ── Reviewed Facilities ────────────────────────────────────────────────────
  // Tracks facility IDs (or TE-IDs) that the user has saved/reviewed after SC standards were updated.
  // This allows us to dismiss the "Facility Standards Need Attention" banner for specific FA/AS/CS facilities.
  reviewedFacilities: string[];
  addReviewedFacility: (facilityId: string) => void;
  clearReviewedFacilities: () => void;

  // ── Persisted session identifier ───────────────────────────────────────────
  // Stored alongside reviewedFacilities so we can detect when SC_ID changes
  // and invalidate the persisted reviewedFacilities automatically.
  persistedScId: string | null;
  setPersistedScId: (scId: string | null) => void;

  // ── SC Details save signal ─────────────────────────────────────────────────
  // Incremented by the SC Details tab after a successful save.
  // The Facility Details tab watches this and re-fetches all facility details
  // so the violation check always sees the latest saved SC standards.
  scDetailsSaveTick: number;
  incrementScDetailsSaveTick: () => void;

  // ── Full transient-state reset ─────────────────────────────────────────────
  // Clears ALL in-memory caches (L1 Zustand). Call at session-start for a new
  // SC_ID, and after a successful final submit so the next SC opens clean.
  clearAllTransientState: () => void;
}

export const useScStore = create<SCState>()(
  persist(
    (set) => ({
  scStandards: [],
  setScStandards: (value: string[]) => set((state) => {
    const oldStandards = [...state.scStandards].sort().join(',');
    const newStandards = [...value].sort().join(',');
    if (oldStandards !== newStandards) {
      return { scStandards: value, reviewedFacilities: [] };
    }
    return { scStandards: value };
  }),
  scStandardDescriptions: {},
  setScStandardDescriptions: (value: Record<string, string>) => set({ scStandardDescriptions: value }),
  editSCInfo: null,
  setEditSCInfo: (value) => set({ editSCInfo: value }),
  hasPR0035: false,
  setHasPR0035: (value: boolean) => set({ hasPR0035: value }),
  scLastAuditStartDate: '',
  setScLastAuditStartDate: (value: string) => set({ scLastAuditStartDate: value }),
  scLastAuditDate: '',
  setScLastAuditDate: (value: string) => set({ scLastAuditDate: value }),

  // SC data cache
  scData: null,
  setScData: (value: any) => set({ scData: value }),
  clearScData: () => set({ scData: null }),

  // Facility list cache
  facilityListCache: null,
  setFacilityListCache: (list: any[]) => set({ facilityListCache: list }),
  clearFacilityListCache: () => set({ facilityListCache: null }),

  facilityCacheTick: 0,
  incrementFacilityCacheTick: () => set((state) => ({ facilityCacheTick: state.facilityCacheTick + 1 })),

  scDetailsSaveTick: 0,
  incrementScDetailsSaveTick: () => set((state) => ({ scDetailsSaveTick: state.scDetailsSaveTick + 1 })),

  // Facility detail cache
  facilityDetailsCache: {},
  setCachedFacilityDetail: (facilityId: string, data: any) =>
    set((state) => ({
      facilityDetailsCache: { ...state.facilityDetailsCache, [facilityId]: data },
    })),
  invalidateFacilityDetail: (facilityId: string) =>
    set((state) => {
      const next = { ...state.facilityDetailsCache };
      delete next[facilityId];
      return { facilityDetailsCache: next };
    }),
  clearFacilityCache: () => set({ facilityDetailsCache: {} }),

  // Per-tab error flags
  tabErrors: {},
  setTabError: (tabId: string, hasError: boolean) =>
    set((state) => ({
      tabErrors: { ...state.tabErrors, [tabId]: hasError },
    })),
  clearTabErrors: () => set({ tabErrors: {} }),

  // Saved claims cache
  savedClaims: [],
  setSavedClaims: (claims: string[]) => set({ savedClaims: claims }),
  clearSavedClaims: () => set({ savedClaims: [] }),

  // Backend Validation Errors cache
  backendValidationErrors: {},
  setBackendValidationErrors: (tabId: string, messages: string[]) =>
    set((state) => ({
      backendValidationErrors: { ...state.backendValidationErrors, [tabId]: messages },
    })),
  clearBackendValidationErrors: () => set({ backendValidationErrors: {} }),

  // Reviewed Facilities cache
  reviewedFacilities: [],
  addReviewedFacility: (facilityId: string) =>
    set((state) => ({
      reviewedFacilities: state.reviewedFacilities.includes(facilityId)
        ? state.reviewedFacilities
        : [...state.reviewedFacilities, facilityId],
    })),
  clearReviewedFacilities: () => set({ reviewedFacilities: [] }),

  // Persisted session identifier
  persistedScId: null,
  setPersistedScId: (scId: string | null) => set({ persistedScId: scId }),

  // Full transient-state reset — clears everything that is SC-session-specific.
  // Called at session start (new SC_ID) and after successful final submit.
  clearAllTransientState: () => set({
    scData: null,
    facilityListCache: null,
    facilityDetailsCache: {},
    savedClaims: [],
    tabErrors: {},
    backendValidationErrors: {},
    scStandards: [],
    scStandardDescriptions: {},
    scLastAuditStartDate: '',
    scLastAuditDate: '',
    hasPR0035: false,
    reviewedFacilities: [],
    facilityCacheTick: 0,
    scDetailsSaveTick: 0,
  }),
    }),
    {
      name: 'sc-standard-storage',
      // Only persist the reviewed-facilities list (scoped to SC_ID) so the
      // "Facility Standards Need Attention" dismissals survive a page refresh.
      // Everything else is transient (fetched fresh on mount).
      partialize: (state) => ({
        reviewedFacilities: state.reviewedFacilities,
        persistedScId: state.persistedScId,
      }),
    }
  )
);
